import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CommonService } from 'src/@services/common.service';
import { FormService } from 'src/@services/form.service';
import { CommonPagesService } from '../../common-pages.service';
import { ResponseStatus } from '../../enum';
import { paybevEnvironment, supplierEnvironment } from 'src/environments/environment';
import { phoneDigits } from './phone-change-verify/phone-change-verify.component';

@Component({
  selector: 'app-myprofile',
  templateUrl: './myprofile.component.html',
  styleUrls: ['./myprofile.component.scss'],
  standalone: false,
})
export class MyprofileComponent implements OnInit {
  isVisibleProfileDrawer = true;
  isLoading = false;
  userDetails: any;
  isUpdatingUser: any;
  uploadResult: any;
  /** Stands in for the picture when there is none, as PayBev's Profile does. */
  userInitials = '';
  /** True once the LinkedIn control holds a valid URL, so "Verify link" never opens a blank tab. */
  canVerifyLinkedIn = false;
  readonly phoneControl: FormControl;
  /** The number on file; a different one must be verified before Update is allowed. */
  savedPhoneNumber: string | null = null;
  _uiPhoneVerifyPending = false;

  @Output() hideProfileDrawer = new EventEmitter();
  constructor(
    public _cs: CommonService,
    public _fS: FormService,
    public _cp: CommonPagesService
  ) {
    _fS.profileForm_fb();
    this.phoneControl = _fS.profileForm.get('phoneNumber') as FormControl;
  }

  ngOnInit(): void {
    this.getUserDetails();
    this._fS.profileForm.get('linkedinUrl')!.valueChanges.subscribe(() => this.refreshLinkedInState());
    this.phoneControl.valueChanges.subscribe(() => this.refreshPhonePending());
  }

  /** The server saved the verified number, so it is now the number on file. */
  onPhoneVerified(phoneNumber: string): void {
    this.savedPhoneNumber = phoneNumber;
    if (this.userDetails) {
      this.userDetails.phoneNumber = phoneNumber;
    }
    this.refreshPhonePending();
  }

  /** First and last initial, falling back to the email so the circle is never blank. */
  private setUserInitials(): void {
    const first: string = this.userDetails?.firstName ?? '';
    const last: string = this.userDetails?.lastName ?? '';
    const email: string = this.userDetails?.email ?? '';
    this.userInitials = (`${first.charAt(0)}${last.charAt(0)}`.trim() || email.charAt(0)).toUpperCase();
  }

  private refreshPhonePending(): void {
    this._uiPhoneVerifyPending = phoneDigits(this.phoneControl.getRawValue()) !== phoneDigits(this.savedPhoneNumber);
  }

  private refreshLinkedInState(): void {
    const control = this._fS.profileForm.get('linkedinUrl');
    this.canVerifyLinkedIn = !!control?.value && !control.errors;
  }

  /** Opens the entered LinkedIn profile in a new tab, as PayBev's User Details does. */
  openLinkedIn(): void {
    const url: string = this._fS.profileForm.get('linkedinUrl')?.value;
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }
  handleCancel() {
    this.isVisibleProfileDrawer = false;
    this.hideProfileDrawer.emit(this.isVisibleProfileDrawer);
  }
  getUserDetails() {
    this.isLoading = true;
    this._cp.getUserProfileData().subscribe(
      (response: any) => {
        if (response.responseStatus == ResponseStatus.success) {
          this.userDetails = response.result;
          this.setUserInitials();
          this.savedPhoneNumber = this.userDetails.phoneNumber;
          this._fS.profileForm.patchValue({
            firstName: this.userDetails.firstName,
            lastName: this.userDetails.lastName,
            phoneNumber: this.userDetails.phoneNumber,
            title: this.userDetails.title,
            location: this.userDetails.location,
            linkedinUrl: this.userDetails.linkedInUrl
          });

          this.refreshLinkedInState();
          this.uploadResult = this.userDetails.profile;
          this._cs.profilePicture.next(this.userDetails.profile ?? null);
          this.isLoading = false;
        } else {
          this.isLoading = false;
        }
      },
      (error: any) => {
        this.isLoading = false;
      }
    );
  }

  onFileChanged(e: any) {
    const file = e?.target?.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.set('File', file);
    this._cp.manageProfilePicture(formData).subscribe(
      (response: any) => {
        if (response.responseStatus == ResponseStatus.success) {
          this.uploadResult = response.result?.profileUrl;
          this.syncProfileCache(this.uploadResult);
          this._cs.displayMessage('success', response.message);
        } else {
          this._cs.displayMessage('error', response.message);
        }
      },
      (error: any) => {
        this._cs.displayMessage('error', error.message);
      }
    );
  }

  removeLogo() {
    const formData = new FormData();
    formData.set('Remove', 'true');
    this._cp.manageProfilePicture(formData).subscribe((response: any) => {
      if (response.responseStatus == ResponseStatus.success) {
        this.uploadResult = null;
        this.syncProfileCache(null);
        this._cs.displayMessage('success', response.message);
      } else {
        this._cs.displayMessage('error', response.message);
      }
    });
  }

  handleOk() {
    if (this._uiPhoneVerifyPending) {
      return;
    }
    if (this._fS.profileForm.valid) {
      this.isUpdatingUser = true;
      const formValue = this._fS.profileForm.getRawValue();
      var profileBody = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        phoneNumber: formValue.phoneNumber,
        title: formValue.title,
        location: formValue.location,
        linkedInUrl: formValue.linkedinUrl,
      };
      this._cp.updateProfileData(profileBody).subscribe((response: any) => {
        if (response.responseStatus == ResponseStatus.success) {
          this.isUpdatingUser = false;
          this.syncProfileCache(this.uploadResult);
          this._cs.displayMessage('success', response.message);
          this.handleCancel();
        } else {
          this._fS.profileForm.patchValue({
            firstName: this.userDetails.firstName,
            lastName: this.userDetails.lastName,
            phoneNumber: this.userDetails.phoneNumber,
            title: this.userDetails.title,
            location: this.userDetails.location,
            linkedinUrl: this.userDetails.linkedInUrl
          });
          this.isUpdatingUser = false;
          this._cs.displayMessage('error', response.message);
        }
      });
    } else {
      this.isUpdatingUser = false;
      this._cs.markFormGroupTouched(this._fS.profileForm);
    }
  }

  private syncProfileCache(profileUrl: string | null | undefined): void {
    if (profileUrl) {
      localStorage.setItem('profile', JSON.stringify(profileUrl));
    } else {
      localStorage.removeItem('profile');
    }
    if (this.userDetails) {
      this.userDetails.profile = profileUrl ?? null;
    }
    this._cs.profilePicture.next(profileUrl ?? null);
  }

  redirecToPaybev(siteId: string, isSupplier: boolean) {
    var encodedRefreshToken: any = btoa(
      unescape(encodeURIComponent(localStorage.getItem('RefreshToken') || ''))
    );
    var userInfo = JSON.parse(localStorage.getItem('UserInfo') || '{}');

    var encodedEmail: any = btoa(unescape(encodeURIComponent(userInfo.email)));
    var woSpaceToken = encodedRefreshToken.replace('=', '');
    var woSpaceEmail = encodedEmail.replace('=', '');

    let linkToOpen = '';
    if (isSupplier) {
      linkToOpen = `${supplierEnvironment.URL}supplier/${siteId
        }/summary?t=${woSpaceToken}&e=${woSpaceEmail}`;
    } else {
      linkToOpen = `${paybevEnvironment.URL}paybev/${siteId
        }/summary?t=${woSpaceToken}&e=${woSpaceEmail}`;
    }
    window.open(linkToOpen, '_blank');
  }
}
